import { useMemo, useState } from 'react';
import {
  GroupedVersionCredits,
  ReleaseType,
  ReleaseVersionContribution,
} from 'types/releases';
import { GenericObject } from 'types/generic.types';
import { useIsMounted } from 'app/utils/hooks';
import { UpdateVersionCreditsEffectData } from 'app/store/Releases/effects.types';

interface UseListColumnsProps {
  credits: GroupedVersionCredits[];
  releaseType: ReleaseType;
  totalWidth?: number;
}

const OPTIONS_SIZE = 50;
const DATA_COMP_SIZE = 44;
const CREDITED_SIZE = 72;

const getColumnSize = (
  percent: number,
  totalWidth?: number,
  showCredited = false
) => {
  if (!totalWidth) {
    return undefined;
  }
  let size = totalWidth - OPTIONS_SIZE;

  if (showCredited) {
    size -= CREDITED_SIZE;
  }

  return size * percent;
};

export const useListColumns = ({
  releaseType,
  totalWidth,
}: UseListColumnsProps) => {
  const showCredited = releaseType === ReleaseType.LABEL;
  return useMemo(
    () => [
      {
        accessorKey: 'role',
        header: 'Contributor',
        minSize: 84,
        size: getColumnSize(1 / 6, totalWidth, showCredited),
        enableResizing: true,
        sx: { pl: 0 },
      },
      {
        accessorKey: 'date',
        header: 'Date Added',
        minSize: 96,
        size: getColumnSize(1 / 6, totalWidth, showCredited),
        enableResizing: true,
      },
      {
        accessorKey: 'country',
        header: 'Country',
        minSize: 72,
        size: getColumnSize(1 / 6, totalWidth, showCredited),
        enableResizing: true,
      },
      {
        accessorKey: 'addedBy',
        header: 'Added By',
        minSize: 80,
        size: getColumnSize(1 / 6, totalWidth, showCredited),
        enableResizing: true,
      },
      {
        accessorKey: 'location',
        header: 'Location',
        minSize: 80,
        size: getColumnSize(1 / 6, totalWidth, showCredited),
        enableResizing: true,
      },
      ...(showCredited
        ? [
            {
              accessorKey: 'credited',
              header: 'Credited',
              size: CREDITED_SIZE,
            },
          ]
        : []),
      {
        accessorKey: 'dataComp',
        header: '',
        size: DATA_COMP_SIZE,
      },
      {
        accessorKey: 'gap',
        size: 0,
      },
      {
        accessorKey: 'options',
        header: '',
        size: OPTIONS_SIZE,
        isOptions: true,
      },
    ],
    [showCredited, totalWidth]
  );
};

interface UseCreditedHandlerProps {
  updateCreditsList: (data: UpdateVersionCreditsEffectData) => Promise<void>;
}

export const useCreditedHandler = ({
  updateCreditsList,
}: UseCreditedHandlerProps) => {
  const isMounted = useIsMounted();
  const [isUpdating, setIsUpdating] = useState<GenericObject<boolean>>({});

  const handleSetIsUpdating = (guid: string, state: boolean) => {
    setIsUpdating({
      [guid]: state,
    });
  };

  const onCreditedChange = async (contribution: ReleaseVersionContribution) => {
    handleSetIsUpdating(contribution.contributionGuid, true);
    await updateCreditsList({
      contributionGuid: contribution.contributionGuid,
      body: {
        isCredited: !contribution.isCredited,
      },
    });
    if (isMounted()) {
      handleSetIsUpdating(contribution.contributionGuid, false);
    }
  };
  return { onCreditedChange, isUpdating };
};
